import { Button, Col, Row } from "reactstrap";
import { Link } from "react-router-dom";
import { useAtomValue } from "jotai";
import { useMemo } from "react";

import { GetTotalNotificationsFromTenant } from "../../components/Navbars/CloudNotifications/NotificationUtils";
import useFavoriteCloud, {
  FavoritePayload,
} from "../../api/api-hooks/cloud/useFavoriteCloud";
import GCSymbol from "../../assets/img/growthcloud/growthcloud_symbol.svg";
import MCCloudIcon from "../../assets/img/icons/common/Icon_Mission_Cloud.svg";
import SUPClodIcon from "../../assets/img/icons/common/Icon_Supportal_Cloud.svg";
import GlobalCloudIcon from "../../assets/img/icons/common/Icon_Global_Cloud.svg";
import useLaunchCloud from "../../api/api-hooks/cloud/useLaunchCloud";
import { callFunctionIfExists } from "../../utils/GeneralUtils";
import useClouds from "../../api/api-hooks/cloud/useClouds";
import { TenantInfo } from "../../types/TenantInterfaces";
import useUser from "../../api/api-hooks/auth/useUser";
import AppSpinner from "../../elements/AppSpinner";
import useHover from "../../hooks/useHover";
import { IsMobileAtom } from "../../App";
import Constants from "../../utils/Constants";

interface Props {
  closeDrawer: any;
  isDrawer: boolean;
  small?: boolean;
  onSelect?: () => void;
  customOnClick?: (cloud: any) => void;
  cloudsToHide?: string[];
  favoritesEnabled: boolean;
}

const CloudList = ({
  closeDrawer = null,
  isDrawer = false, // Changes styles slightly, and what happens when launching a cloud
  small = false, // True case is used in the Mobile Sidebar Switcher
  onSelect,
  customOnClick,
  cloudsToHide,
  favoritesEnabled, // Whether or not favorites can be selected, and sorted into a favorites group
}: Props) => {
  let { clouds, cloudsLoading } = useClouds();
  const { user } = useUser();
  const { launchCloud, launching } = useLaunchCloud();
  const { favoriteCloud } = useFavoriteCloud();

  // Assort clouds into favorites group if favorites are enabled
  const finalClouds = useMemo(() => {
    if (!clouds || !clouds.length) return { clouds: [], favorites: [] };
    if (!favoritesEnabled) return { clouds: clouds, favorites: [] };

    const favorites: TenantInfo[] = [];
    const otherClouds: TenantInfo[] = [];

    clouds.forEach((cloud) => {
      // Legacy property for supportal
      if (cloud.TenantID === "GLOBAL") return;

      if (cloud.Favorite) {
        favorites.push(cloud);
      } else {
        otherClouds.push(cloud);
      }
    });

    return { clouds: otherClouds, favorites: favorites };
  }, [clouds, favoritesEnabled, user?.Notifications]);

  return (
    <div style={{ overflowY: "auto" }}>
      {/* TODO: Error Handling */}
      {/* {launchError && <Alert color="warning">{launchError}</Alert>} */}
      {cloudsLoading ? (
        <div className="p-4 border rounded-sm bg-ultralight">
          <h4 className="mb-1">Hang Tight...</h4>
          <p className="text-muted mb-0">We're fetching your clouds.</p>
        </div>
      ) : clouds && clouds.length > 0 ? (
        <>
          {favoritesEnabled && !!finalClouds.favorites.length && (
            <h5 className={isDrawer ? "" : "mt-4 ml-4"}>Favorites</h5>
          )}
          {favoritesEnabled &&
            finalClouds.favorites.map((c, i) => {
              if (cloudsToHide && cloudsToHide?.includes(c?.TenantID)) {
                return null;
              }
              if (c.TenantID === "GLOBAL") return null;

              return (
                <CloudListItem
                  key={c.TenantID}
                  clouds={clouds ?? []}
                  cloud={c}
                  launchCloud={launchCloud}
                  launching={launching}
                  i={i}
                  favoriteCloud={favoriteCloud}
                  small={small}
                  onSelect={onSelect}
                  customOnClick={customOnClick}
                  favoritesEnabled={favoritesEnabled}
                  isDrawer={isDrawer}
                  closeDrawer={closeDrawer}
                  notifications={GetTotalNotificationsFromTenant(
                    c.TenantID,
                    user
                  )}
                />
              );
            })}
          {favoritesEnabled &&
            !!finalClouds.favorites.length &&
            !!finalClouds.clouds.length && (
              <h5 className={isDrawer ? "mt-4" : "mt-4 ml-4"}>Other Clouds</h5>
            )}
          {finalClouds.clouds.map((c, i: number) => {
            if (cloudsToHide && cloudsToHide?.includes(c?.TenantID)) {
              return null;
            }
            if (c.TenantID === "GLOBAL") return null;

            return (
              <CloudListItem
                key={c.TenantID}
                clouds={clouds ?? []}
                // @ts-ignore
                cloud={c}
                launchCloud={launchCloud}
                launching={launching}
                i={i}
                favoriteCloud={favoriteCloud}
                small={small}
                onSelect={onSelect}
                customOnClick={customOnClick}
                favoritesEnabled={favoritesEnabled}
                isDrawer={isDrawer}
                closeDrawer={closeDrawer}
                notifications={GetTotalNotificationsFromTenant(
                  c.TenantID,
                  user
                )}
              />
            );
          })}
        </>
      ) : (
        <div className="p-4">
          <Row className="align-items-center">
            <Col>
              <h4 className="mb-1">
                You haven&apos;t been invited to a Cloud yet
              </h4>
              <p className="text-muted mb-0">
                Contact an administrator to be invited to your Cloud.
              </p>
            </Col>
          </Row>
        </div>
      )}
    </div>
  );
};

interface ItemProps extends Props {
  clouds: TenantInfo[];
  cloud: TenantInfo;
  launchCloud: any;
  launching: string | null;
  favoriteCloud: (payload: { payload: FavoritePayload }) => void;
  i: number;
  notifications: number;
}

function CloudListItem({
  clouds,
  cloud,
  launchCloud,
  launching,
  i,
  favoriteCloud,
  small,
  onSelect,
  customOnClick,
  favoritesEnabled,
  isDrawer,
  closeDrawer,
  notifications,
}: ItemProps) {
  const isMobile = useAtomValue(IsMobileAtom);
  const [itemRef, isHovered] = useHover<HTMLAnchorElement>();

  const isGlobal = cloud?.GlobalAppBuilder;
  const cloudID = cloud?.TenantID;

  const icon = useMemo(() => {
    if (cloud.TenantID === Constants.MC_CLOUD_ID) {
      return MCCloudIcon;
    } else if (cloud.TenantID === Constants.SUP_CLOUD_ID) {
      return SUPClodIcon;
    } else if (isGlobal) {
      return GlobalCloudIcon;
    } else {
      return GCSymbol;
    }
  }, [cloudID, isGlobal]);

  return (
    <Link
      to={`/clouds/${cloud?.TenantID}/workspaces`}
      ref={itemRef}
      data-test="cloud-list-item"
      className={`cursor-pointer d-flex justify-content-between align-items-center bg-ultralight--hover ${
        small && "cloud-list-item-small"
      } ${
        i !== clouds?.length - 1
          ? `${small ? "" : "border-bottom border-superlight"} `
          : ""
      } ${
        isDrawer
          ? "px-3 py-2 mb-1 border border-superlight"
          : small
          ? "pl-2 mx-2"
          : "px-4 py-3 "
      }`}
      style={
        isDrawer
          ? { borderRadius: 6 }
          : small
          ? {
              paddingTop: "0.5rem",
              paddingBottom: "0.5rem",
              borderRadius: 5,
            }
          : undefined
      }
      onClick={
        customOnClick
          ? (e) => {
              e.preventDefault();
              customOnClick(cloud);
            }
          : (e) => {
              e.preventDefault();
              launchCloud({
                userTenant: cloud,
                isDrawer: isDrawer,
                onClose: closeDrawer,
              });
              callFunctionIfExists(onSelect);
            }
      }
    >
      <div className="tw:flex tw:items-center">
        <img
          src={icon}
          alt=""
          className={"mr-2 d-sm-inline-block"}
          style={{ width: small ? 20 : 30 }}
        />
        <h3
          className={`text-left text-sm-left m-0 font-weight-500 ml-2 ${
            small && "text-dark"
          }`}
          style={{ fontSize: isMobile ? 15 : small ? 13 : undefined }}
        >
          {cloud ? cloud.TenantName : ""}
        </h3>
      </div>
      {/* Cloud Card Buttons */}
      <div className="tw:flex tw:items-center">
        {favoritesEnabled && !launching && isHovered && (
          <Button
            size="sm"
            style={
              small
                ? {
                    height: "1.5rem",
                    width: "1.5rem",
                    position: "relative",
                  }
                : undefined
            }
            color="transparent"
            outline
            onClick={(e) => {
              e?.preventDefault();
              e.stopPropagation();
              favoriteCloud({
                payload: {
                  cloudID: cloud.TenantID,
                  action: cloud.Favorite ? "remove" : "add",
                },
              });
            }}
            className="btn-icon-only m-0"
          >
            <i
              className={`cursor-pointer text-primary mdi mdi-heart${
                cloud.Favorite ? "" : "-outline"
              }`}
            />
          </Button>
        )}
        {notifications !== 0 && (
          <div className="position-relative">
            <i className="mdi mdi-bell text-lg text-medium px-2" />
            <div
              className="position-absolute text-white bg-primary rounded font-weight-bold"
              style={{
                top: 2,
                right: notifications > 99 ? -5 : notifications < 10 ? 7 : 0,
                lineHeight: 1.1,
                fontSize: 10,
                padding: "0 4px",
              }}
            >
              {notifications > 99 ? "99+" : notifications}
            </div>
          </div>
        )}
        <Button
          size="sm"
          style={
            small
              ? {
                  height: "1.5rem",
                  width: "1.5rem",
                  position: "relative",
                }
              : undefined
          }
          color="secondary"
          outline
          onClick={() => {
            launchCloud({
              userTenant: cloud,
              isDrawer: isDrawer,
              onClose: closeDrawer,
            });
            callFunctionIfExists(onSelect);
          }}
          disabled={!!launching || launching === cloud.TenantID}
          className="btn-icon-only"
        >
          {launching === cloud.TenantID ? (
            <AppSpinner />
          ) : (
            <i
              className="mdi mdi-chevron-right"
              style={
                small
                  ? {
                      position: "absolute",
                      top: 3,
                      left: 2,
                      fontSize: 20,
                    }
                  : undefined
              }
            />
          )}
        </Button>
      </div>
    </Link>
  );
}

export default CloudList;
